import { supabase } from '../lib/supabase'
import type { Intervalo } from '../utils/periodo'

export type Conversao = { id: string; nome: string; principal: boolean }

export type AcaoConversao = {
  id: string
  nome: string
  idExterno: string
  categoria: string | null
  contaId: string
  contaNome: string
  plataforma: string
  demo: boolean
}

export type ConfiguracaoMarca = {
  conversoes: Conversao[]
  acoes: AcaoConversao[]
  /** conversão → ações que a compõem */
  vinculos: Map<string, Set<string>>
  /** ação → quantidade registrada no período de referência */
  uso: Map<string, number>
  /** Cliques nos anúncios da marca no mesmo período (para comparar com as conversões). */
  cliques: number
}

const erro = (e: { message: string; code?: string } | null) => {
  if (!e) return
  // 23505 = unique violation: o único caso é nome repetido na mesma marca.
  if (e.code === '23505') throw new Error('Já existe uma conversão com esse nome nesta marca')
  throw new Error(e.message)
}

export type ConversaoPrincipal = {
  marcaId: string
  nome: string
  /** De quais plataformas vêm as ações que compõem a principal (ex.: meta_ads, ga4). */
  plataformas: Set<string>
}

/**
 * A conversão principal de cada marca visível, com as plataformas das suas ações — cada
 * painel precisa saber se a principal tem alguma ação da fonte dele.
 */
export async function carregarConversoesPrincipais(): Promise<ConversaoPrincipal[]> {
  const { data, error } = await supabase
    .from('conversoes')
    .select('marca_id, nome, conversao_acoes(acoes_conversao(contas(plataforma)))')
    .eq('principal', true)
  erro(error)
  type Linha = { marca_id: string; nome: string; conversao_acoes: Array<{ acoes_conversao: { contas: { plataforma: string } } }> }
  return ((data ?? []) as unknown as Linha[]).map((c) => ({
    marcaId: c.marca_id,
    nome: c.nome,
    plataformas: new Set(c.conversao_acoes.map((v) => v.acoes_conversao.contas.plataforma)),
  }))
}

export async function carregarConfiguracao(marcaId: string, periodoUso: Intervalo): Promise<ConfiguracaoMarca> {
  const [conv, acoes, vinc, uso, totais] = await Promise.all([
    supabase.from('conversoes').select('id, nome, principal').eq('marca_id', marcaId).order('nome'),
    supabase
      .from('acoes_conversao')
      .select('id, nome, id_externo, categoria, contas(id, nome, plataforma, demo)')
      .eq('marca_id', marcaId)
      .order('nome'),
    supabase.from('conversao_acoes').select('conversao_id, acao_id').eq('marca_id', marcaId),
    supabase.rpc('acoes_conversao_uso', { p_marca: marcaId, p_inicio: periodoUso.inicio, p_fim: periodoUso.fim }),
    supabase.rpc('trafego_pago_totais', { p_inicio: periodoUso.inicio, p_fim: periodoUso.fim, p_marca: marcaId }),
  ])
  erro(conv.error)
  erro(acoes.error)
  erro(vinc.error)
  erro(uso.error)
  erro(totais.error)

  const vinculos = new Map<string, Set<string>>()
  for (const v of vinc.data ?? []) {
    const lista = vinculos.get(v.conversao_id) ?? new Set<string>()
    lista.add(v.acao_id)
    vinculos.set(v.conversao_id, lista)
  }

  return {
    conversoes: (conv.data ?? []) as Conversao[],
    acoes: (acoes.data ?? []).map((a) => {
      // contas vem como objeto (FK para uma linha só); o tipo gerado não sabe disso.
      const conta = a.contas as unknown as { id: string; nome: string; plataforma: string; demo: boolean }
      return {
        id: a.id,
        nome: a.nome,
        idExterno: a.id_externo,
        categoria: a.categoria,
        contaId: conta.id,
        contaNome: conta.nome,
        plataforma: conta.plataforma,
        demo: conta.demo,
      }
    }),
    vinculos,
    uso: new Map(((uso.data ?? []) as Array<{ acao_id: string; quantidade: number }>).map((u) => [u.acao_id, Number(u.quantidade)])),
    cliques: Number((totais.data as Array<{ cliques: number }> | null)?.[0]?.cliques ?? 0),
  }
}

export async function criarConversao(marcaId: string, nome: string, principal: boolean) {
  const { error } = await supabase.from('conversoes').insert({ marca_id: marcaId, nome: nome.trim(), principal })
  erro(error)
}

export async function renomearConversao(id: string, nome: string) {
  const { error } = await supabase.from('conversoes').update({ nome: nome.trim() }).eq('id', id)
  erro(error)
}

export async function apagarConversao(id: string) {
  const { error } = await supabase.from('conversoes').delete().eq('id', id)
  erro(error)
}

/** Troca a principal numa transação só (função no banco). */
export async function definirPrincipal(id: string) {
  const { error } = await supabase.rpc('definir_conversao_principal', { p_conversao: id })
  erro(error)
}

export async function incluirAcao(conversaoId: string, acaoId: string, marcaId: string) {
  const { error } = await supabase
    .from('conversao_acoes')
    .insert({ conversao_id: conversaoId, acao_id: acaoId, marca_id: marcaId })
  erro(error)
}

export async function retirarAcao(conversaoId: string, acaoId: string) {
  const { error } = await supabase.from('conversao_acoes').delete().eq('conversao_id', conversaoId).eq('acao_id', acaoId)
  erro(error)
}

// ---------------------------------------------------------------------------
// Metas da marca (sobre a conversão principal)
// ---------------------------------------------------------------------------

export type MetaMarca = { custoPorConversao: number | null; roas: number | null }

export async function carregarMeta(marcaId: string): Promise<MetaMarca> {
  const { data, error } = await supabase.from('metas_marca').select('custo_por_conversao_alvo, roas_alvo').eq('marca_id', marcaId).maybeSingle()
  erro(error)
  return {
    custoPorConversao: data?.custo_por_conversao_alvo == null ? null : Number(data.custo_por_conversao_alvo),
    roas: data?.roas_alvo == null ? null : Number(data.roas_alvo),
  }
}

/** Sem nenhuma das duas, a meta sai. O RLS barra em silêncio: confere a linha gravada. */
export async function salvarMeta(marcaId: string, m: MetaMarca) {
  if (m.custoPorConversao == null && m.roas == null) {
    const { error } = await supabase.from('metas_marca').delete().eq('marca_id', marcaId)
    erro(error)
    return
  }
  const { data, error } = await supabase
    .from('metas_marca')
    .upsert({ marca_id: marcaId, custo_por_conversao_alvo: m.custoPorConversao, roas_alvo: m.roas, atualizado_em: new Date().toISOString() })
    .select('marca_id')
  if (error?.code === '23514') throw new Error('As metas precisam ser maiores que zero')
  erro(error)
  if (!data?.length) throw new Error('Você não tem permissão para alterar as metas desta marca')
}
